import { Component, EventEmitter, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomerService } from '../../../core/services/customer.service';
import { Customer, CreateCustomerDto } from '../../../core/models/customer.model';
import { NgxMaskDirective } from 'ngx-mask';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

@Component({
  selector: 'app-customer-quick-add',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgxMaskDirective, MaxLengthDirective],
  templateUrl: './customer-quick-add.component.html',
  styleUrls: ['./customer-quick-add.component.scss']
})
export class CustomerQuickAddComponent {
  private fb = inject(FormBuilder);
  private customerService = inject(CustomerService);

  @Output() customerCreated = new EventEmitter<Customer>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  isSaving = signal(false);
  isOpen = signal(false);
  apiError = signal<string | null>(null);

  constructor() {
    this.initForm();
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      phone: [''],
      email: ['', [Validators.email]],
    });
  }

  open(): void {
    this.isOpen.set(true);
    this.apiError.set(null);
    this.form.reset();
  }

  close(): void {
    this.isOpen.set(false);
    this.apiError.set(null);
    this.form.reset();
    this.cancelled.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.close();
    }
  }

  async onSubmit(): Promise<void> {
    // Trim name before validation so whitespace-only input is caught
    const nameControl = this.form.get('name');
    if (nameControl?.value) {
      nameControl.setValue(nameControl.value.trim());
    }

    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    this.isSaving.set(true);
    this.apiError.set(null);

    const formData = this.form.getRawValue();
    const dto: CreateCustomerDto = {
      name: formData.name,
      phone: formData.phone?.trim() || undefined,
      email: formData.email?.trim() || undefined,
    };

    try {
      const newCustomer = await this.customerService.createCustomer(dto);
      if (newCustomer) {
        this.isOpen.set(false);
        this.form.reset();
        this.customerCreated.emit(newCustomer);
      } else {
        this.apiError.set('Failed to create customer. Please try again.');
      }
    } catch (error: any) {
      this.apiError.set(error?.message || 'Failed to create customer');
    } finally {
      this.isSaving.set(false);
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }
}
